/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Combobox,
    createTableColumn,
    DataGridCell,
    DataGridHeaderCell,
    Dropdown,
    makeStyles,
    Option,
    SearchBox,
    Spinner,
    tokens,
} from '@fluentui/react-components';
import { Open24Regular } from '@fluentui/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { IDataItem } from '../../models/IDataItem';
import type { ProjektArtItem } from '../../models/ProjektArtItem';
import type { ProjektInstitutItem } from '../../models/ProjektInstitutItem';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';

const ROW_HEIGHT = 40; // px

const EXCEL_COLUMNS: ExcelExportColumn<ProjektItem>[] = [
    { header: 'Project ID', value: (item) => item.projektID, width: 12 },
    { header: 'Project Title', value: (item) => item.title, width: 50 },
    { header: 'Status', value: (item) => item.projektstatusInternBezeichnung },
    { header: 'Project Type', value: (item) => item.projektart },
    { header: 'Project Manager', value: (item) => item.projektleiterName, width: 25 },
    { header: 'Archived/Cancelled', value: (item) => item.archivedAsText },
    { header: 'Department', value: (item) => item.department, width: 30 },
    { header: 'Institute', value: (item) => item.departmentDepartment, width: 40 },
    { header: 'Start Date', value: (item) => item.createdAsText },
    { header: 'Cost Center', value: (item) => item.kostentraegerNummer },
    { header: 'External Project Number', value: (item) => item.externeProjektnummer, width: 25 },
    { header: 'Funding Provider', value: (item) => item.foerdergeber, width: 30 },
    { header: 'Funding Track', value: (item) => item.foerderschiene, width: 30 },
];

// Column weight configuration for proportional widths
const COLUMN_WEIGHTS: Record<string, number> = {
    action: 0.4,
    projektID: 0.6,
    title: 2,
    projektstatusInternBezeichnung: 0.9,
    projektart: 0.9,
    projektleiterName: 1.1,
    archived: 1,
    department: 1,
    departmentDepartment: 1.2,
    createdAsText: 0.8,
    kostentraegerNummer: 0.9,
    externeProjektnummer: 1,
    foerdergeber: 1,
    foerderschiene: 0.9,
};

const COLUMN_ORDER = [
    'action',
    'projektID',
    'title',
    'projektstatusInternBezeichnung',
    'projektart',
    'projektleiterName',
    'archived',
    'department',
    'departmentDepartment',
    'createdAsText',
    'kostentraegerNummer',
    'externeProjektnummer',
    'foerdergeber',
    'foerderschiene',
] as const;

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyStateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        textAlign: 'center',
    },
    filterSearchSection: {
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
    },
    searchBoxWrapper: {
        display: 'flex',
        width: '100%',
        marginBottom: '4px',
    },
    filterContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '8px',
        alignItems: 'flex-end',
        width: '100%',
    },
    filterContainerWithButton: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px',
        alignItems: 'flex-end',
        gridAutoFlow: 'row',
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    filterLabel: {
        fontSize: '12px',
        fontWeight: '500',
        color: tokens.colorNeutralForeground1,
    },
    searchBox: {
        gridColumn: '1 / -1',
    },
    searchButton: {
        gridColumn: '1 / -1',
        alignSelf: 'flex-end',
        width: 'fit-content',
    },
    separator: {
        height: '1px',
        backgroundColor: tokens.colorNeutralStroke1,
        margin: '12px 0',
    },
    contentArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        overflow: 'auto',
    },
    fundingRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        gridColumn: '1 / -1',
    },
    statusTypeRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        gridColumn: '1 / -1',
    },
    departmentManagerRow: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '12px',
        gridColumn: '1 / -1',
    },
    dataGridContainer: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
});

const Archive: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();

    const [items, setItems] = useState<ProjektItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // ===== SEARCH SECTION STATE VARIABLES (Server-side API filters) =====
    const [searchText, setSearchText] = useState('');
    const [searchStatusFilterValue, setSearchStatusFilterValue] = useState<number | 'All'>('All');
    const [searchDepartmentFilterValue, setSearchDepartmentFilterValue] = useState<number | 'All'>('All');
    const [searchProjectTypeFilterValue, setSearchProjectTypeFilterValue] = useState<number | 'All'>('All');
    const [searchProjectManagerFilterValue, setSearchProjectManagerFilterValue] = useState<number | 'All'>('All');
    const [searchFundingProviderFilterValue, setSearchFundingProviderFilterValue] = useState<number | 'All'>('All');
    const [searchFundingTrackFilterValue, setSearchFundingTrackFilterValue] = useState<number | 'All'>('All');
    const [searchProjectManagerInputValue, setSearchProjectManagerInputValue] = useState('');
    const [searchProjectManagerResults, setSearchProjectManagerResults] = useState<ProjektmitarbeiterItem[]>([]);

    // ===== POST-SEARCH FILTER SECTION STATE VARIABLES (Client-side result filters) =====
    const [filterText, setFilterText] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [departmentFilter, setDepartmentFilter] = useState('All');
    const [institutFilter, setInstitutFilter] = useState('All');
    const [projektArtFilter, setProjektArtFilter] = useState('All');
    const [foerdergeberFilter, setFoerdergeberFilter] = useState('All');
    const [foerschreieneFilter, setFoerschreieneFilter] = useState('All');
    const [mitarbeiterFilter, setMitarbeiterFilter] = useState('All');

    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [projektArtOptions, setProjektArtOptions] = useState<ProjektArtItem[]>([]);
    const [statusOptions, setStatusOptions] = useState<IDataItem[]>([]);
    const [instituteOptions, setInstituteOptions] = useState<ProjektInstitutItem[]>([]);
    const [mitarbeiterOptions, setMitarbeiterOptions] = useState<ProjektmitarbeiterItem[]>([]);
    const [foerdergeberOptions, setFoerdergeberOptions] = useState<IDataItem[]>([]);
    const [foerderschieneOptions, setFoerderschieneOptions] = useState<IDataItem[]>([]);
    const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);
    const optionsFetchedRef = React.useRef(false);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Handler to apply fetch filters and search
    const handleApplyFetchFilters = async () => {
        if (!dataService) return;

        try {
            setIsLoading(true);
            const filterRequest: any = {
                projektIds: [],
                searchText: searchText,
                statusFilter: searchStatusFilterValue === 'All' ? undefined : searchStatusFilterValue,
                institutFilter: searchDepartmentFilterValue === 'All' ? undefined : searchDepartmentFilterValue,
                projektArtFilter: searchProjectTypeFilterValue === 'All' ? undefined : searchProjectTypeFilterValue,
                foerdergeberFilter: searchFundingProviderFilterValue === 'All' ? undefined : searchFundingProviderFilterValue,
                foerderschreieneFilter: searchFundingTrackFilterValue === 'All' ? undefined : searchFundingTrackFilterValue,
                mitarbeiterFilter: searchProjectManagerFilterValue === 'All' ? undefined : searchProjectManagerFilterValue,
            };
            const data = await dataService.getProjektListeArchiviertFiltered(filterRequest);
            setItems(data || []);
            setHasSearched(true);
        } catch (err) {
            console.error('Error fetching archived projects:', err);
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Handler to search project managers by name
    const handleProjectManagerSearch = (searchValue: string) => {
        if (searchValue.length < 2) {
            setSearchProjectManagerResults([]);
            return;
        }
        const lowerSearchValue = searchValue.toLowerCase();
        const filtered = mitarbeiterOptions.filter((member) => {
            const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
            return fullName.toLowerCase().includes(lowerSearchValue);
        });
        setSearchProjectManagerResults(filtered);
    };

    const totalWeight = useMemo(() => {
        return COLUMN_ORDER.reduce((sum, columnId) => {
            return sum + (COLUMN_WEIGHTS[columnId as keyof typeof COLUMN_WEIGHTS] || 1);
        }, 0);
    }, []);

    const getColumnWidth = (columnId: string) => {
        const weight = COLUMN_WEIGHTS[columnId] || 1;
        const percentageWidth = (weight / totalWeight) * 100;
        return percentageWidth;
    };

    // Calculate dynamic grid height
    const gridHeight = React.useMemo(() => {
        return Math.max(windowHeight * 0.65, 300); // Min 300px
    }, [windowHeight]);

    React.useEffect(() => {
        const handleWindowResize = () => {
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleWindowResize);
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    // Fetch options on component mount (once only)
    useEffect(() => {
        const fetchOptions = async () => {
            if (!dataService || optionsFetchedRef.current) return;

            try {
                const [artenData, instituteData, statusData, mitarbeiterData, foerdergeberData, foerderschieneData] = await Promise.all([
                    dataService.getProjektArtenArchiv(),
                    dataService.getProjektInstitute(),
                    dataService.getProjektStatusItems(),
                    dataService.getMitarbeiter(),
                    dataService.getFoerdergeberNeu(),
                    dataService.getFoerderschieneItems(),
                ]);

                setProjektArtOptions(artenData || []);
                setInstituteOptions(instituteData || []);
                setStatusOptions(statusData || []);
                setMitarbeiterOptions(mitarbeiterData || []);
                setFoerdergeberOptions(foerdergeberData || []);
                setFoerderschieneOptions(foerderschieneData || []);
                optionsFetchedRef.current = true;
            } catch (err) {
                console.error('Error fetching filter options:', err);
            }
        };

        fetchOptions();
    }, [dataService]);

    // Get unique values for dropdowns
    const uniqueStatuses = ['All', ...new Set(items.map((item) => item.projektstatusInternBezeichnung).filter(Boolean))];
    const uniqueDepartments = ['All', ...new Set(items.map((item) => item.department).filter(Boolean))];
    const uniqueInstitute = ['All', ...new Set(items.map((item) => item.departmentDepartment).filter(Boolean))];
    const uniqueFoerdergeber = ['All', ...new Set(items.map((item) => item.foerdergeber).filter(Boolean))];
    const uniqueFoerschreieine = ['All', ...new Set(items.map((item) => item.foerderschiene).filter(Boolean))];
    const uniqueMitarbeiter = ['All', ...new Set(items.map((item) => item.projektleiterName).filter(Boolean))];

    const searchTerm = filterText.trim();
    // Filter items based on all criteria
    const filteredItems = items.filter((item) => {
        const matchesText =
            searchTerm.length <= 2 ||
            item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.kostentraegerNummer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.projektleiterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.projektart?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.projektID?.toString().includes(searchTerm) ||
            item.foerdergeber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.foerderschiene?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'All' || item.projektstatusInternBezeichnung === statusFilter;
        const matchesDepartment = departmentFilter === 'All' || item.department === departmentFilter;
        const matchesInstitut = institutFilter === 'All' || item.departmentDepartment === institutFilter;
        const matchesProjektArt = projektArtFilter === 'All' || item.projektart === projektArtFilter;
        const matchesFoerdergeber = foerdergeberFilter === 'All' || item.foerdergeber === foerdergeberFilter;
        const matchesFoerschreieine = foerschreieneFilter === 'All' || item.foerderschiene === foerschreieneFilter;
        const matchesMitarbeiter = mitarbeiterFilter === 'All' || item.projektleiterName === mitarbeiterFilter;

        return matchesText && matchesStatus && matchesDepartment && matchesInstitut && matchesProjektArt && matchesFoerdergeber && matchesFoerschreieine && matchesMitarbeiter;
    });

    // Sort items
    const sortedItems = useMemo(() => {
        if (!sortColumn) return filteredItems;

        const sorted = [...filteredItems].sort((a, b) => {
            const aValue = (a as any)[sortColumn];
            const bValue = (b as any)[sortColumn];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
            if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }

            return sortDirection === 'asc'
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });

        return sorted;
    }, [filteredItems, sortColumn, sortDirection]);

    const handleSort = React.useCallback((columnId: string) => {
        if (sortColumn === columnId) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(columnId);
            setSortDirection('asc');
        }
    }, [sortColumn, sortDirection]);

    const handleOpenProject = React.useCallback((item: ProjektItem) => {
        if (item.projektID) {
            // Set navigation origin to archive
            sessionStorage.setItem('navigationOrigin', 'archive');
            navigate(`/project/${item.projektID}`);
        }
    }, [navigate]);

    const columns = React.useMemo(
        () => [
            createTableColumn<ProjektItem>({
                columnId: 'action',
                renderHeaderCell: () => '',
                renderCell: (item) => (
                    <button
                        onClick={() => handleOpenProject(item)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        title="Open project"
                    >
                        <Open24Regular style={{ color: tokens.colorBrandForeground1 }} />
                    </button>
                ),
            }),
            createTableColumn<ProjektItem>({
                columnId: 'projektID',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('projektID')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'projektID' ? 'bold' : 'normal' }}>
                        Project ID {sortColumn === 'projektID' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.projektID?.toString() || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'title',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('title')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'title' ? 'bold' : 'normal' }}>
                        Project Title {sortColumn === 'title' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.title || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'projektstatusInternBezeichnung',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('projektstatusInternBezeichnung')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'projektstatusInternBezeichnung' ? 'bold' : 'normal' }}>
                        Status {sortColumn === 'projektstatusInternBezeichnung' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.projektstatusInternBezeichnung || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'projektart',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('projektart')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'projektart' ? 'bold' : 'normal' }}>
                        Project Type {sortColumn === 'projektart' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.projektart || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'projektleiterName',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('projektleiterName')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'projektleiterName' ? 'bold' : 'normal' }}>
                        Project Manager {sortColumn === 'projektleiterName' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.projektleiterName || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'archived',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('archived')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'archived' ? 'bold' : 'normal' }}>
                        Archived/Cancelled {sortColumn === 'archived' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => {
                    if (!item.archived) return '-';
                    if (!item.archivedAsText) return '-';
                    return `${item.archivedAsText} (${item.storniertArchiviert ? 'Cancelled' : 'Archived'})`;
                },
            }),
            createTableColumn<ProjektItem>({
                columnId: 'department',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('department')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'department' ? 'bold' : 'normal' }}>
                        Department {sortColumn === 'department' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.department || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'departmentDepartment',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('departmentDepartment')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'departmentDepartment' ? 'bold' : 'normal' }}>
                        Institute {sortColumn === 'departmentDepartment' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.departmentDepartment || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'createdAsText',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('createdAsText')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'createdAsText' ? 'bold' : 'normal' }}>
                        Start Date {sortColumn === 'createdAsText' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.createdAsText || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'kostentraegerNummer',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('kostentraegerNummer')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'kostentraegerNummer' ? 'bold' : 'normal' }}>
                        Cost Carrier Number {sortColumn === 'kostentraegerNummer' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.kostentraegerNummer || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'externeProjektnummer',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('externeProjektnummer')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'externeProjektnummer' ? 'bold' : 'normal' }}>
                        External Project Number {sortColumn === 'externeProjektnummer' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.externeProjektnummer || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'foerdergeber',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('foerdergeber')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'foerdergeber' ? 'bold' : 'normal' }}>
                        Funding Provider {sortColumn === 'foerdergeber' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.foerdergeber || '-',
            }),
            createTableColumn<ProjektItem>({
                columnId: 'foerderschiene',
                renderHeaderCell: () => (
                    <div onClick={() => handleSort('foerderschiene')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'foerderschiene' ? 'bold' : 'normal' }}>
                        Funding Track {sortColumn === 'foerderschiene' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </div>
                ),
                renderCell: (item) => item.foerderschiene || '-',
            }),
        ],
        [handleSort, handleOpenProject, sortColumn, sortDirection]
    );

    return (
        <div className={styles.container}>
            {!hasSearched ? (
                // Show fetch filters before search
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', marginTop: '20px', width: '100%' }}>
                    <h2 style={{ color: tokens.colorNeutralForeground2, margin: 0 }}>
                        Search Archived Projects
                    </h2>
                    <div className={styles.filterSearchSection} style={{ width: '100%' }}>
                        <SearchBox
                            className={styles.searchBox}
                            placeholder="Search by title, project manager, project ID..."
                            onChange={(_, newValue) => setSearchText(newValue?.value || '')}
                            value={searchText}
                            style={{ width: '100%' }}
                        />
                        <div className={styles.filterContainer}>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>
                                    Status
                                </label>
                                <Dropdown
                                    placeholder="All"
                                    value={
                                        searchStatusFilterValue === 'All'
                                            ? 'All'
                                            : statusOptions.find((s) => s.value === searchStatusFilterValue.toString())?.text || 'All'
                                    }
                                    onOptionSelect={(_e, option) => {
                                        if (option.optionValue === 'All') {
                                            setSearchStatusFilterValue('All');
                                        } else {
                                            const parsed = parseInt(option.optionValue || '0');
                                            setSearchStatusFilterValue(isNaN(parsed) ? 'All' : parsed);
                                        }
                                    }}
                                >
                                    <Option value="All">All</Option>
                                    {statusOptions.map((status) => (
                                        <Option key={status.value} value={status.value.toString()}>
                                            {status.text}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>
                                    Department
                                </label>
                                <Dropdown
                                    placeholder="All"
                                    value={
                                        searchDepartmentFilterValue === 'All'
                                            ? 'All'
                                            : instituteOptions.find((i) => i.id === searchDepartmentFilterValue)?.bezeichnung || 'All'
                                    }
                                    onOptionSelect={(_e, option) => {
                                        if (option.optionValue === 'All') {
                                            setSearchDepartmentFilterValue('All');
                                        } else {
                                            const parsed = parseInt(option.optionValue || '0');
                                            setSearchDepartmentFilterValue(isNaN(parsed) ? 'All' : parsed);
                                        }
                                    }}
                                >
                                    <Option value="All">All</Option>
                                    {instituteOptions.map((inst) => (
                                        <Option key={inst.id} value={inst.id.toString()}>
                                            {inst.bezeichnung}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>
                                    Project Type
                                </label>
                                <Dropdown
                                    placeholder="All"
                                    value={
                                        searchProjectTypeFilterValue === 'All'
                                            ? 'All'
                                            : projektArtOptions.find((a) => a.projektartID === searchProjectTypeFilterValue)?.bezeichnung || 'All'
                                    }
                                    onOptionSelect={(_e, option) => {
                                        if (option.optionValue === 'All') {
                                            setSearchProjectTypeFilterValue('All');
                                        } else {
                                            const parsed = parseInt(option.optionValue || '0');
                                            setSearchProjectTypeFilterValue(isNaN(parsed) ? 'All' : parsed);
                                        }
                                    }}
                                >
                                    <Option value="All">All</Option>
                                    {projektArtOptions.map((art) => (
                                        <Option key={art.projektartID} value={art.projektartID.toString()}>
                                            {art.bezeichnung}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>
                                    Project Manager
                                </label>
                                <Combobox
                                    placeholder="Enter at least 2 characters..."
                                    value={searchProjectManagerInputValue}
                                    onOptionSelect={(_e, option) => {
                                        const memberId = parseInt(option.optionValue || '');
                                        const member = mitarbeiterOptions.find((m) => m.personalID === memberId);
                                        if (member) {
                                            const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                            setSearchProjectManagerInputValue(fullName);
                                            setSearchProjectManagerFilterValue(memberId);
                                        }
                                    }}
                                    onChange={(ev) => {
                                        const searchValue = (ev.target as HTMLInputElement).value;
                                        setSearchProjectManagerInputValue(searchValue);
                                        if (searchValue === '') {
                                            setSearchProjectManagerFilterValue('All');
                                        }
                                        handleProjectManagerSearch(searchValue);
                                    }}
                                    appearance="outline"
                                >
                                    {searchProjectManagerResults.map((member) => {
                                        const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                        return (
                                            <Option
                                                key={member.personalID}
                                                value={member.personalID.toString()}
                                                text={fullName}
                                            >
                                                {fullName}
                                            </Option>
                                        );
                                    })}
                                </Combobox>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>
                                    Funding Provider
                                </label>
                                <Dropdown
                                    placeholder="All"
                                    value={
                                        searchFundingProviderFilterValue === 'All'
                                            ? 'All'
                                            : foerdergeberOptions.find((f) => parseInt(f.value) === searchFundingProviderFilterValue)?.text || 'All'
                                    }
                                    onOptionSelect={(_e, option) => {
                                        if (option.optionValue === 'All') {
                                            setSearchFundingProviderFilterValue('All');
                                        } else {
                                            const parsed = parseInt(option.optionValue || '0');
                                            setSearchFundingProviderFilterValue(isNaN(parsed) ? 'All' : parsed);
                                        }
                                    }}
                                >
                                    <Option value="All">All</Option>
                                    {foerdergeberOptions.map((foe) => (
                                        <Option key={foe.value} value={foe.value.toString()}>
                                            {foe.text}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>
                                    Funding Track
                                </label>
                                <Dropdown
                                    placeholder="All"
                                    value={
                                        searchFundingTrackFilterValue === 'All'
                                            ? 'All'
                                            : foerderschieneOptions.find((f) => parseInt(f.value) === searchFundingTrackFilterValue)?.text || 'All'
                                    }
                                    onOptionSelect={(_e, option) => {
                                        if (option.optionValue === 'All') {
                                            setSearchFundingTrackFilterValue('All');
                                        } else {
                                            const parsed = parseInt(option.optionValue || '0');
                                            setSearchFundingTrackFilterValue(isNaN(parsed) ? 'All' : parsed);
                                        }
                                    }}
                                >
                                    <Option value="All">All</Option>
                                    {foerderschieneOptions.map((fs) => (
                                        <Option key={fs.value} value={fs.value.toString()}>
                                            {fs.text}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                        </div>
                        <Button
                            appearance="primary"
                            onClick={handleApplyFetchFilters}
                            disabled={isLoading}
                            style={{ marginTop: '8px', alignSelf: 'flex-start' }}
                        >
                            {isLoading ? <Spinner size="small" /> : 'Search'}
                        </Button>
                    </div>
                </div>
            ) : (
                // Show results with search section on top, content area below
                <>
                    <div className={styles.filterSearchSection}>

                        <div style={{ marginBottom: '16px', marginTop: '16px' }}>
                            <Button
                                appearance="secondary"
                                onClick={() => setHasSearched(false)}
                            >
                                Edit Search Filters
                            </Button>
                        </div>
                        <div className={styles.searchBoxWrapper}>
                            <SearchBox
                                placeholder="Search by title, project manager, project ID..."
                                onChange={(_, newValue) => setFilterText(newValue?.value || '')}
                                value={filterText}
                                style={{ flex: 1 }}
                            />
                        </div>
                        <div className={styles.filterContainer}>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Status</label>
                                <Dropdown
                                    placeholder="All"
                                    value={statusFilter}
                                    onOptionSelect={(_e, option) =>
                                        setStatusFilter(option.optionValue || 'All')
                                    }
                                >
                                    {uniqueStatuses.map((status) => (
                                        <Option key={status} value={status}>
                                            {status}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Department</label>
                                <Dropdown
                                    placeholder="All"
                                    value={departmentFilter}
                                    onOptionSelect={(_e, option) =>
                                        setDepartmentFilter(option.optionValue || 'All')
                                    }
                                >
                                    {uniqueDepartments.map((dept) => (
                                        <Option key={dept} value={dept}>
                                            {dept}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Institute</label>
                                <Dropdown
                                    placeholder="All"
                                    value={institutFilter}
                                    onOptionSelect={(_e, option) =>
                                        setInstitutFilter(option.optionValue || 'All')
                                    }
                                >
                                    {uniqueInstitute.map((inst) => (
                                        <Option key={inst} value={inst}>
                                            {inst}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Project Type</label>
                                <Dropdown
                                    placeholder="All"
                                    value={projektArtFilter}
                                    onOptionSelect={(_e, option) =>
                                        setProjektArtFilter(option.optionValue || 'All')
                                    }
                                >
                                    <Option value="All">All</Option>
                                    {projektArtOptions.map((art) => (
                                        <Option key={art.projektartID} value={art.bezeichnung || ''}>
                                            {art.bezeichnung}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Funding Provider</label>
                                <Dropdown
                                    placeholder="All"
                                    value={foerdergeberFilter}
                                    onOptionSelect={(_e, option) =>
                                        setFoerdergeberFilter(option.optionValue || 'All')
                                    }
                                >
                                    {uniqueFoerdergeber.map((foe) => (
                                        <Option key={foe} value={foe}>
                                            {foe}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Funding Track</label>
                                <Dropdown
                                    placeholder="All"
                                    value={foerschreieneFilter}
                                    onOptionSelect={(_e, option) =>
                                        setFoerschreieneFilter(option.optionValue || 'All')
                                    }
                                >
                                    {uniqueFoerschreieine.map((foe) => (
                                        <Option key={foe} value={foe}>
                                            {foe}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Project Manager</label>
                                <Dropdown
                                    placeholder="All"
                                    value={mitarbeiterFilter}
                                    onOptionSelect={(_e, option) =>
                                        setMitarbeiterFilter(option.optionValue || 'All')
                                    }
                                >
                                    {uniqueMitarbeiter.map((mitarbeiter) => (
                                        <Option key={mitarbeiter} value={mitarbeiter}>
                                            {mitarbeiter}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filterGroup} style={{ alignItems: 'flex-start' }}>
                                <ExcelExportButton
                                    items={sortedItems}
                                    columns={EXCEL_COLUMNS}
                                    fileName={`Archive_${new Date().toISOString().split('T')[0]}`}
                                    sheetName="Archive"
                                />
                            </div>
                        </div>
                        {items.length > 0 && (
                            <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground2, marginTop: '8px' }}>
                                Found {items.length} project{items.length !== 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                    <div className={styles.separator} />
                    <div className={styles.contentArea}>
                        {isLoading ? (
                            <div className={styles.loadingContainer}>
                                <Spinner label="Loading archived projects..." labelPosition="below" />
                            </div>
                        ) : sortedItems.length === 0 ? (
                            <div className={styles.emptyStateContainer}>
                                <h2 style={{ color: tokens.colorNeutralForeground3 }}>
                                    No Archiv Items found
                                </h2>
                            </div>
                        ) : (
                            <div className={styles.dataGridContainer}>
                                {/* Header Row */}
                                <div
                                    style={{
                                        display: 'flex',
                                        width: '100%',
                                        backgroundColor: tokens.colorNeutralBackground3,
                                        borderBottom: '1px solid #d1d5db',
                                        fontWeight: 600,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10,
                                    }}
                                >
                                    {COLUMN_ORDER.map((columnId) => {
                                        const column = columns.find((col) => col.columnId === columnId);
                                        if (!column) return null;
                                        const width = getColumnWidth(columnId);
                                        return (
                                            <DataGridHeaderCell
                                                key={columnId}
                                                className="dataGridHeaderCell"
                                                style={{
                                                    flex: `0 0 ${width}%`,
                                                    paddingLeft: '8px',
                                                    paddingRight: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    borderRight: '1px solid #e1e1e1',
                                                    boxSizing: 'border-box',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {column.renderHeaderCell()}
                                            </DataGridHeaderCell>
                                        );
                                    })}
                                </div>

                                {/* Flex-based rows with dynamic height */}
                                <div style={{ height: gridHeight, width: '100%', overflowY: 'auto' }}>
                                    {sortedItems.map((item, rowIndex) => (
                                        <div
                                            key={rowIndex}
                                            style={{
                                                display: 'flex',
                                                height: ROW_HEIGHT,
                                                borderBottom: '1px solid #e1e1e1',
                                            }}
                                        >
                                            {COLUMN_ORDER.map((columnId) => {
                                                const column = columns.find((col) => col.columnId === columnId);
                                                const width = getColumnWidth(columnId);
                                                if (!column) return null;
                                                return (
                                                    <DataGridCell
                                                        key={columnId}
                                                        style={{
                                                            flex: `0 0 ${width}%`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            paddingLeft: '8px',
                                                            paddingRight: '8px',
                                                            borderRight: '1px solid #e1e1e1',
                                                            boxSizing: 'border-box',
                                                            cursor: 'pointer',
                                                            overflow: 'hidden',
                                                        }}
                                                        onClick={() => handleOpenProject(item)}
                                                        role="gridcell"
                                                    >
                                                        {column.renderCell(item)}
                                                    </DataGridCell>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div >
    );
};

export default Archive;
